import axios from 'axios';
import {
  ChidoriStateResponse,
  ExposeChidoriCredentialsResponse,
} from 'types/spark';
import { API_URL } from 'utils/http';

export function submitChidori(values, instance) {
  return axios.post(`${API_URL}/spark/chidori`, {
    values,
    instanceName: instance.name,
    userID: instance.userID,
  });
}

export async function fetchInstanceChidoriStatus(instanceId) {
  try {
    const res = await axios.get<ChidoriStateResponse>(
      `${API_URL}/spark/fetchChidori`,
      {
        params: { instanceId },
      },
    );
    return res.data.chidoriState;
  } catch (e) {
    throw new Error(e);
  }
}

export async function exposeChidoriCredentials(instanceName: string) {
  try {
    const res = await axios.post<ExposeChidoriCredentialsResponse>(
      `${API_URL}/spark/expose/chidori`,
      {
        instanceName,
      },
    );
    return res.data;
  } catch (e) {
    throw new Error(e);
  }
}

export async function disableChidoriPublicApi(instanceName: string) {
  try {
    const res = await axios.delete(
      `${API_URL}/spark/apis/chidori/${instanceName}`,
      {},
    );
    return res.data;
  } catch (e) {
    throw new Error(e);
  }
}

export type chidoriSizeItem = {
  id: number;
  size: string;
  sizeValue: number;
  createdAt: Date;
  updatedAt: Date;
};
type chidoriSizesResult = {
  allChidoriSizesInTable: {
    count: number;
    rows: chidoriSizeItem[];
  };
  total: number;
  limit: number;
  page: number;
  pagesCount: number;
};
export async function getChidoriSizes({ page = 1, search, userid = null }) {
  try {
    const res = await axios.get<chidoriSizesResult>(
      `${API_URL}/spark/getChidoriSizesTable`,
      {
        params: { page, search, userid },
      },
    );
    return res.data.allChidoriSizesInTable.rows;
  } catch (e) {
    throw new Error(e);
  }
}

export type modifiedChidoriSize = { name: string; memory: string };
export async function updateChidoriSizes({
  chidoriSizes,
}: {
  chidoriSizes: modifiedChidoriSize[];
}) {
  try {
    const res = await axios.post<any>(
      `${API_URL}/spark/updateChidoriSizesTable`,
      {
        chidoriSizes,
      },
    );
    return res;
  } catch (e) {
    throw new Error(e);
  }
}

export function upgradeChidori(instanceName: string, userID: string, target?: string) {
  return axios.post(`${API_URL}/spark/upgrade/chidori`, {
    instanceName,
    userID,
    target,
  });
}

export function restartChidori(instanceName: string, userID: string) {
  return axios.post(`${API_URL}/spark/restart/chidori`, {
    instanceName,
    userID,
  });
}

export function setChidoriMemoryLimit(instanceName: string, userID: string, memoryLimitGB: number) {
  return axios.post(`${API_URL}/spark/chidori/memory`, {
    instanceName,
    userID,
    memoryLimitGB,
  });
}

export interface ChidoriBusy {
  driverProcesses: number;
  driverPods: number;
  queuedJobs: number;
  busy: boolean;
}

export interface ChidoriUpgradeProgress {
  startedAt: string;
  target: string;
  phase: 'draining' | 'applying' | 'restarting' | 'done';
  attempt: number;
  busy: ChidoriBusy | null;
  outcome: 'succeeded' | 'busy-timeout' | 'failed' | null;
  message: string | null;
  stamp: string | null;
  updatedAt: string;
}

export interface ChidoriStatus {
  pod: { name: string; phase: string; ready: boolean; restartStamp: string | null } | null;
  busy: ChidoriBusy | null;
  upgrade: {
    status: string;
    upgradedAt: string | null;
    updatedAt: string;
    last: ChidoriUpgradeProgress | null;
  } | null;
  memory: {
    chidoriVersion: string;
    memoryLimitGB: number;
    isCustom: boolean;
    size: string;
    maxMemoryLimitGB: number;
  } | null;
}

export function getChidoriStatus(instanceName: string, userID: string, busy: boolean) {
  return axios.get<ChidoriStatus>(`${API_URL}/spark/chidori/status`, {
    params: { instanceName, userID, busy },
  });
}
